import React, { useEffect, useMemo, useRef, useState } from "react";
import { MENU_ITEMS, MENU_CATEGORIES } from "../../config/menuConfig";

/**
 * AdminMenuVisibility — 메뉴 권한 관리 (LOGINS 풍 좌/우 split)
 *
 * 좌측: 탭(그룹/사용자) + 검색 + 다중 선택 그리드 (shift+click 범위 선택)
 * 우측: 카테고리별 메뉴 + 일괄 저장 / 상속 초기화
 *
 * 정책:
 *   - MANAGER_YN='Y' 사용자는 항상 전체 노출 (visibility 무시)
 *   - 일반 사용자 우선순위: user > group > global > 코드 기본값
 *
 * 동작 (다중 선택 / touched 로직):
 *   - 좌측 1+개 선택: 선택된 scope 들의 현재 override 를 병합해 우측에 표시
 *     · 모두 같은 값 → 그 값 (show/hide/inherit) 표시
 *     · 다른 값 → "혼합" 표시 (사용자가 토글 안 하면 각 scope 값 유지)
 *   - 사용자가 라디오 클릭한 메뉴(touched)만 저장 대상
 *   - 저장: PUT /api/admin/menu-override-bulk (선택된 모든 scope 에 동일 권한)
 *   - 상속 초기화: 선택된 모든 scope 의 override row 전체 삭제 (visible=null)
 */
const AdminMenuVisibility = ({ API_BASE_URL }) => {
  const [scopeTab, setScopeTab] = useState("group"); // 'group' | 'user'
  const [groups, setGroups] = useState([]);
  const [users, setUsers] = useState([]);
  const [filter, setFilter] = useState(""); // 통합 검색 (legacy — 인라인 필터로 대체됐지만 호환 유지)
  const [colFilters, setColFilters] = useState({
    code: "",
    name: "",
    meta: "",
  }); // 컬럼별 인라인 필터
  const [sortKey, setSortKey] = useState("code"); // 'code' | 'name' | 'meta'
  const [sortDir, setSortDir] = useState("asc"); // 'asc' | 'desc'
  const [popoverGroupId, setPopoverGroupId] = useState(null); // 인원 클릭 시 사용자 list 표시할 그룹
  const [selected, setSelected] = useState([]);
  const [overrideDraft, setOverrideDraft] = useState({});
  const [touched, setTouched] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [savedAt, setSavedAt] = useState(null);
  const [globalVisibility, setGlobalVisibility] = useState({});

  const lastClickedIndexRef = useRef(-1);
  const adminToken = () => localStorage.getItem("admin_token");

  // 그룹 list + 전역 visibility
  useEffect(() => {
    (async () => {
      try {
        const [gRes, vRes] = await Promise.all([
          fetch(`${API_BASE_URL}/api/admin/companies`, {
            headers: { Authorization: `Bearer ${adminToken()}` },
          }),
          fetch(`${API_BASE_URL}/api/admin/menu-visibility`, {
            headers: { Authorization: `Bearer ${adminToken()}` },
          }),
        ]);
        const gData = await gRes.json();
        const vData = await vRes.json();
        if (gData.success) setGroups(gData.data || []);
        if (vData.success) {
          const map = {};
          for (const it of vData.items || [])
            map[it.menuKey] = !!it.visibleToUser;
          setGlobalVisibility(map);
        }
      } catch {
        setError("서버 연결 실패");
      }
    })();
  }, [API_BASE_URL]);

  // 사용자 list — 그룹 탭에서도 인원 popover 에 이름 표시하므로 mount 시 1회 로드
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/admin/super-login/users`, {
          headers: { Authorization: `Bearer ${adminToken()}` },
        });
        const data = await res.json();
        if (data.success) setUsers(data.data || []);
      } catch {
        /* ignore */
      }
    })();
  }, [API_BASE_URL]);

  // 그룹별 사용자 list (인원 popover 용)
  const usersByGroup = useMemo(() => {
    const map = {};
    for (const u of users) {
      const k = u.groupCode;
      if (!map[k]) map[k] = [];
      map[k].push(u);
    }
    return map;
  }, [users]);

  // 좌측 탭 변경 시 선택 초기화
  useEffect(() => {
    setSelected([]);
    setOverrideDraft({});
    setTouched(new Set());
    lastClickedIndexRef.current = -1;
  }, [scopeTab]);

  // 선택 변경 시: 모든 scope 의 override 를 fetch + merge
  useEffect(() => {
    setTouched(new Set());
    if (selected.length === 0) {
      setOverrideDraft({});
      return;
    }
    (async () => {
      setLoading(true);
      setError("");
      try {
        const scopes = selected.map(parseRowKey);
        const results = await Promise.all(
          scopes.map(async ({ groupId, userId }) => {
            const qs = new URLSearchParams({ groupId });
            if (userId) qs.set("userId", userId);
            const r = await fetch(
              `${API_BASE_URL}/api/admin/menu-override?${qs}`,
              { headers: { Authorization: `Bearer ${adminToken()}` } },
            );
            const d = await r.json();
            const map = {};
            for (const it of d.items || [])
              map[it.menuKey] = it.visible ? "show" : "hide";
            return map;
          }),
        );
        // merge: 각 메뉴마다 모든 scope 의 상태 모으기
        const merged = {};
        for (const m of MENU_ITEMS) {
          const vals = results.map((r) => r[m.id] || "inherit");
          const uniq = Array.from(new Set(vals));
          merged[m.id] = uniq.length === 1 ? uniq[0] : "mixed";
        }
        setOverrideDraft(merged);
      } catch {
        setError("서버 연결 실패");
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line
  }, [selected, scopeTab]);

  const parseRowKey = (rowKey) => {
    if (scopeTab === "group") return { groupId: rowKey, userId: null };
    const [groupId, userId] = rowKey.split("||");
    return { groupId, userId };
  };

  // 좌측 그리드 row list — 통합검색 + 컬럼별 인라인 필터 + 정렬
  const rows = useMemo(() => {
    const q = filter.trim().toLowerCase();
    const cf = {
      code: colFilters.code.trim().toLowerCase(),
      name: colFilters.name.trim().toLowerCase(),
      meta: colFilters.meta.trim().toLowerCase(),
    };
    const norm = (s) => (s == null ? "" : String(s).toLowerCase());

    let arr = [];
    if (scopeTab === "group") {
      arr = groups.map((g) => ({
        rowKey: g.GROUP_ID,
        groupId: g.GROUP_ID,
        col1: g.GROUP_ID,
        col2: g.COMPANY_NAME || "(미등록)",
        col3Label: `${g.USER_COUNT}명`,
        col3Sort: Number(g.USER_COUNT) || 0,
      }));
    } else {
      arr = users.map((u) => ({
        rowKey: `${u.groupCode}||${u.userId}`,
        groupId: u.groupCode,
        userId: u.userId,
        col1: u.groupCode,
        col2: u.companyName || "(미등록)",
        col3Label: `${u.userId}${u.userName ? ` · ${u.userName}` : ""}${u.managerYn === "Y" ? " " : ""}`,
        col3Sort: `${u.userId}`,
      }));
    }

    // 통합 검색 (legacy — colFilters 와 AND)
    if (q) {
      arr = arr.filter(
        (r) =>
          norm(r.col1).includes(q) ||
          norm(r.col2).includes(q) ||
          norm(r.col3Label).includes(q),
      );
    }
    // 컬럼별 필터
    if (cf.code) arr = arr.filter((r) => norm(r.col1).includes(cf.code));
    if (cf.name) arr = arr.filter((r) => norm(r.col2).includes(cf.name));
    if (cf.meta) arr = arr.filter((r) => norm(r.col3Label).includes(cf.meta));

    // 정렬
    const dir = sortDir === "asc" ? 1 : -1;
    arr.sort((a, b) => {
      let av, bv;
      if (sortKey === "code") {
        av = a.col1;
        bv = b.col1;
      } else if (sortKey === "name") {
        av = a.col2;
        bv = b.col2;
      } else {
        av = a.col3Sort;
        bv = b.col3Sort;
      }
      if (typeof av === "number" && typeof bv === "number")
        return (av - bv) * dir;
      return String(av).localeCompare(String(bv), "ko") * dir;
    });
    return arr;
  }, [scopeTab, groups, users, filter, colFilters, sortKey, sortDir]);

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortKey(key);
      setSortDir("asc");
    }
  };
  const sortArrow = (key) =>
    sortKey !== key ? "" : sortDir === "asc" ? " ▲" : " ▼";

  // 저장하지 않은 변경이 있으면 confirm. 진행 OK 면 true.
  const confirmUnsaved = () => {
    if (touched.size === 0) return true;
    return window.confirm(
      `저장하지 않은 변경 ${touched.size}개가 있습니다.\n계속하면 변경 내용이 사라집니다.`,
    );
  };

  const switchScopeTab = (tab) => {
    if (tab === scopeTab) return;
    if (!confirmUnsaved()) return;
    setScopeTab(tab);
  };

  const toggleRow = (rowKey, index, e) => {
    if (!confirmUnsaved()) return;
    if (
      e?.shiftKey &&
      lastClickedIndexRef.current >= 0 &&
      lastClickedIndexRef.current !== index
    ) {
      // shift+click 범위: last~current 사이를 전부 추가
      const start = Math.min(lastClickedIndexRef.current, index);
      const end = Math.max(lastClickedIndexRef.current, index);
      const range = rows.slice(start, end + 1).map((r) => r.rowKey);
      setSelected((prev) => {
        const set = new Set(prev);
        for (const k of range) set.add(k);
        return Array.from(set);
      });
      lastClickedIndexRef.current = index;
      return;
    }
    setSelected((prev) => {
      const idx = prev.indexOf(rowKey);
      if (idx >= 0) return prev.filter((k) => k !== rowKey);
      return [...prev, rowKey];
    });
    lastClickedIndexRef.current = index;
  };

  const selectAllVisible = () => {
    if (!confirmUnsaved()) return;
    setSelected(rows.map((r) => r.rowKey));
    lastClickedIndexRef.current = -1;
  };
  const clearSelection = () => {
    if (!confirmUnsaved()) return;
    setSelected([]);
    lastClickedIndexRef.current = -1;
  };

  const setRowOverride = (menuKey, value) => {
    setOverrideDraft((p) => ({ ...p, [menuKey]: value }));
    setTouched((p) => {
      const next = new Set(p);
      next.add(menuKey);
      return next;
    });
  };

  // 카테고리 일괄 적용 — 카테고리 내 모든 메뉴를 같은 상태로
  const applyCategoryState = (categoryId, value) => {
    if (selected.length === 0) {
      setError("좌측에서 대상을 먼저 선택하세요.");
      return;
    }
    const targets = MENU_ITEMS.filter((m) => m.category === categoryId);
    setOverrideDraft((p) => {
      const next = { ...p };
      for (const m of targets) next[m.id] = value;
      return next;
    });
    setTouched((p) => {
      const next = new Set(p);
      for (const m of targets) next.add(m.id);
      return next;
    });
  };

  // 페이지 떠나기 전 경고 (브라우저 새로고침 / 탭 닫기)
  useEffect(() => {
    if (touched.size === 0) return;
    const handler = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [touched]);

  // 저장: touched 메뉴만 보냄
  const handleSave = async () => {
    if (selected.length === 0) {
      setError("좌측에서 대상을 1개 이상 선택해주세요.");
      return;
    }
    if (touched.size === 0) {
      setError("우측에서 변경할 메뉴를 1개 이상 클릭해주세요.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const scopes = selected.map(parseRowKey);
      const items = [];
      for (const menuKey of touched) {
        const state = overrideDraft[menuKey];
        if (state === "show") items.push({ menuKey, visible: true });
        else if (state === "hide") items.push({ menuKey, visible: false });
        else if (state === "inherit") items.push({ menuKey, visible: null });
        // 'mixed' 는 사용자가 안 건드린 것 — touched 에 들어오지 않음
      }
      const res = await fetch(`${API_BASE_URL}/api/admin/menu-override-bulk`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${adminToken()}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ scopes, items }),
      });
      const data = await res.json();
      if (data.success) {
        setSavedAt(new Date());
        setTouched(new Set());
        // 다시 fetch 해서 merge 갱신
        const sel = selected;
        setSelected([]);
        setTimeout(() => setSelected(sel), 0);
      } else {
        setError(data.message || "저장 실패");
      }
    } catch {
      setError("서버 연결 실패");
    } finally {
      setSaving(false);
    }
  };

  const handleResetToInherit = async () => {
    if (selected.length === 0) {
      setError("좌측에서 대상을 1개 이상 선택해주세요.");
      return;
    }
    if (
      !window.confirm(
        `선택된 ${selected.length}개 대상의 모든 메뉴 권한을 '상속'(전역 default 사용)으로 되돌립니다.`,
      )
    )
      return;
    setSaving(true);
    setError("");
    try {
      const scopes = selected.map(parseRowKey);
      const items = MENU_ITEMS.map((m) => ({ menuKey: m.id, visible: null }));
      const res = await fetch(`${API_BASE_URL}/api/admin/menu-override-bulk`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${adminToken()}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ scopes, items }),
      });
      const data = await res.json();
      if (data.success) {
        setSavedAt(new Date());
        setTouched(new Set());
        const sel = selected;
        setSelected([]);
        setTimeout(() => setSelected(sel), 0);
      } else {
        setError(data.message || "초기화 실패");
      }
    } catch {
      setError("서버 연결 실패");
    } finally {
      setSaving(false);
    }
  };

  const groupedMenus = useMemo(() => {
    const out = {};
    for (const cat of MENU_CATEGORIES) out[cat.id] = [];
    for (const m of MENU_ITEMS)
      (out[m.category] || (out[m.category] = [])).push(m);
    return out;
  }, []);

  const touchedCount = touched.size;
  const isMulti = selected.length > 1;

  return (
    <div style={S.root}>
      <div style={S.header}>
        <h2 style={S.title}>메뉴 권한 관리</h2>
        <p style={S.subtitle}>
          좌측에서 대상을 선택하고(shift+click 범위 선택 지원) 우측에서 메뉴를
          토글한 후 저장하세요. 다중 선택 시 변경한 메뉴만 일괄 적용됩니다.
          우선순위 = <b>사용자별 ▶ 그룹별 ▶ 전역</b> ▶ 기본값. 관리자(
          <code style={S.code}>MANAGER_YN='Y'</code>)는 설정 무관 전체 노출.
        </p>
      </div>

      {error && <div style={S.error}>{error}</div>}

      <div style={S.body}>
        {/* ── 좌측 그리드 ── */}
        <div style={S.left}>
          <div style={S.leftTabs}>
            <button
              onClick={() => switchScopeTab("group")}
              style={{
                ...S.leftTab,
                ...(scopeTab === "group" ? S.leftTabActive : {}),
              }}
            >
              그룹별
            </button>
            <button
              onClick={() => switchScopeTab("user")}
              style={{
                ...S.leftTab,
                ...(scopeTab === "user" ? S.leftTabActive : {}),
              }}
            >
              개인별
            </button>
          </div>

          <div style={S.leftToolbar}>
            <input
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder={
                scopeTab === "group"
                  ? "회사명 / 코드 검색"
                  : "이름 / ID / 회사명"
              }
              style={S.search}
            />
            <button
              onClick={selectAllVisible}
              style={S.btnGhost}
              title="현재 필터 보이는 행 전체 선택"
            >
              전체
            </button>
            <button onClick={clearSelection} style={S.btnGhost}>
              해제
            </button>
          </div>

          <div style={S.leftCount}>
            {rows.length}개 · <b>{selected.length}개 선택</b>
            {selected.length > 0 && (
              <span style={S.hint}> · shift+클릭으로 범위 선택</span>
            )}
          </div>

          <div style={S.leftList}>
            <table style={S.leftTable}>
              <thead>
                <tr>
                  <th style={S.thCheck}></th>
                  <th
                    style={{ ...S.thCode, cursor: "pointer" }}
                    onClick={() => toggleSort("code")}
                  >
                    그룹{sortArrow("code")}
                  </th>
                  <th
                    style={{ ...S.thName, cursor: "pointer" }}
                    onClick={() => toggleSort("name")}
                  >
                    {scopeTab === "group" ? "회사명" : "회사 / 사용자"}
                    {sortArrow("name")}
                  </th>
                  <th
                    style={{ ...S.thMeta, cursor: "pointer" }}
                    onClick={() => toggleSort("meta")}
                  >
                    {scopeTab === "group" ? "인원" : "ID · 이름"}
                    {sortArrow("meta")}
                  </th>
                </tr>
                {/* 인라인 필터 row */}
                <tr>
                  <th style={S.thFilterCell}></th>
                  <th style={S.thFilterCell}>
                    <input
                      value={colFilters.code}
                      onChange={(e) =>
                        setColFilters((p) => ({ ...p, code: e.target.value }))
                      }
                      placeholder="필터"
                      style={S.filterInput}
                    />
                  </th>
                  <th style={S.thFilterCell}>
                    <input
                      value={colFilters.name}
                      onChange={(e) =>
                        setColFilters((p) => ({ ...p, name: e.target.value }))
                      }
                      placeholder="필터"
                      style={S.filterInput}
                    />
                  </th>
                  <th style={S.thFilterCell}>
                    <input
                      value={colFilters.meta}
                      onChange={(e) =>
                        setColFilters((p) => ({ ...p, meta: e.target.value }))
                      }
                      placeholder="필터"
                      style={S.filterInput}
                    />
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td colSpan={4} style={S.emptyRow}>
                      데이터가 없습니다
                    </td>
                  </tr>
                ) : (
                  rows.map((r, i) => {
                    const isSel = selected.includes(r.rowKey);
                    const isGroupTab = scopeTab === "group";
                    const popoverOpen = popoverGroupId === r.groupId;
                    const groupUsers = isGroupTab
                      ? usersByGroup[r.groupId] || []
                      : [];
                    return (
                      <tr
                        key={r.rowKey}
                        style={isSel ? S.trSelected : undefined}
                        onClick={(e) => toggleRow(r.rowKey, i, e)}
                      >
                        <td style={S.tdCheck}>
                          <input
                            type="checkbox"
                            checked={isSel}
                            onChange={() => {}}
                            style={S.checkbox}
                          />
                        </td>
                        <td style={S.tdCode}>{r.col1}</td>
                        <td style={S.tdName}>{r.col2}</td>
                        <td
                          style={{
                            ...S.tdMeta,
                            position: "relative",
                            cursor:
                              isGroupTab && groupUsers.length > 0
                                ? "pointer"
                                : "default",
                          }}
                          onClick={(e) => {
                            if (!isGroupTab) return;
                            e.stopPropagation();
                            setPopoverGroupId(popoverOpen ? null : r.groupId);
                          }}
                          onMouseLeave={() =>
                            popoverOpen && setPopoverGroupId(null)
                          }
                          title={
                            isGroupTab && groupUsers.length > 0
                              ? "클릭하면 사용자 이름 보기"
                              : ""
                          }
                        >
                          <span
                            style={
                              isGroupTab && groupUsers.length > 0
                                ? S.metaLink
                                : undefined
                            }
                          >
                            {r.col3Label}
                          </span>
                          {isGroupTab &&
                            popoverOpen &&
                            groupUsers.length > 0 && (
                              <div
                                style={S.popover}
                                onClick={(e) => e.stopPropagation()}
                              >
                                <div style={S.popoverHeader}>
                                  {r.col2} · {groupUsers.length}명
                                </div>
                                <div style={S.popoverList}>
                                  {groupUsers.map((u) => (
                                    <div key={u.userId} style={S.popoverItem}>
                                      <code style={S.popoverUserId}>
                                        {u.userId}
                                      </code>
                                      <span style={S.popoverUserName}>
                                        {u.userName || "-"}
                                        {u.managerYn === "Y" && (
                                          <span style={S.tagManager}>
                                            관리자
                                          </span>
                                        )}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* ── 우측 메뉴 권한 ── */}
        <div style={S.right}>
          <div style={S.rightHeader}>
            <span style={S.rightTitle}>
              권한 설정{" "}
              {selected.length > 0 && (
                <span style={S.rightBadge}>{selected.length}개 대상</span>
              )}
            </span>
            <span style={S.rightHint}>
              {selected.length === 0 && "좌측에서 대상을 선택하세요"}
              {selected.length === 1 &&
                (() => {
                  const r = rows.find((rr) => rr.rowKey === selected[0]);
                  if (!r) return "현재 권한 표시 — 변경 후 저장";
                  return `${r.col2} · ${r.col1}${scopeTab === "user" ? ` · ${r.col3}` : ""}`;
                })()}
              {selected.length > 1 &&
                `${selected.length}개 대상 권한 병합 표시 (혼합 메뉴는 클릭한 것만 일괄 적용)`}
            </span>
          </div>

          {loading ? (
            <div style={S.loading}>불러오는 중...</div>
          ) : (
            <div style={S.menuPanel}>
              {MENU_CATEGORIES.map((cat) => (
                <div key={cat.id} style={S.catBlock}>
                  <div style={S.catHeader}>
                    <span>{cat.label}</span>
                    <span style={S.catActions}>
                      <button
                        onClick={() => applyCategoryState(cat.id, "inherit")}
                        style={S.catBtn}
                        disabled={selected.length === 0 || saving}
                        title="이 카테고리 메뉴 모두 상속으로"
                      >
                        상속
                      </button>
                      <button
                        onClick={() => applyCategoryState(cat.id, "show")}
                        style={{
                          ...S.catBtn,
                          color: "#10b981",
                          borderColor: "#a7f3d0",
                        }}
                        disabled={selected.length === 0 || saving}
                      >
                        전체 노출
                      </button>
                      <button
                        onClick={() => applyCategoryState(cat.id, "hide")}
                        style={{
                          ...S.catBtn,
                          color: "#ef4444",
                          borderColor: "#fecaca",
                        }}
                        disabled={selected.length === 0 || saving}
                      >
                        전체 숨김
                      </button>
                    </span>
                  </div>
                  <div style={S.catItems}>
                    {(groupedMenus[cat.id] || []).map((m) => {
                      const state = overrideDraft[m.id] || "inherit";
                      const isTouched = touched.has(m.id);
                      const isMixed = state === "mixed";
                      const globalOn = !!globalVisibility[m.id];
                      const defaultOn = !m.hiddenByDefault;
                      const fallback = Object.prototype.hasOwnProperty.call(
                        globalVisibility,
                        m.id,
                      )
                        ? globalOn
                        : defaultOn;
                      return (
                        <div
                          key={m.id}
                          style={{
                            ...S.menuRow,
                            ...(isTouched ? S.menuRowTouched : {}),
                            ...(isMixed && !isTouched ? S.menuRowMixed : {}),
                          }}
                        >
                          <div style={S.menuRowText}>
                            <div style={S.menuLabel}>
                              {m.label}
                              {!m.hiddenByDefault && (
                                <span style={S.tagDefaultOn}>기본 노출</span>
                              )}
                              {isMixed && !isTouched && (
                                <span style={S.tagMixed}>혼합</span>
                              )}
                              {isTouched && (
                                <span style={S.tagTouched}>변경</span>
                              )}
                            </div>
                            <code style={S.menuId}>{m.id}</code>
                          </div>
                          <div style={S.radioGroup}>
                            {[
                              {
                                val: "inherit",
                                label: `상속 (${fallback ? "노출" : "숨김"})`,
                                color: "#6b7280",
                              },
                              { val: "show", label: "노출", color: "#10b981" },
                              { val: "hide", label: "숨김", color: "#ef4444" },
                            ].map((opt) => {
                              const isActive = state === opt.val;
                              return (
                                <label
                                  key={opt.val}
                                  style={{
                                    ...S.radioOpt,
                                    ...(isActive
                                      ? {
                                          borderColor: opt.color,
                                          color: opt.color,
                                          fontWeight: 600,
                                          background: "#fff",
                                        }
                                      : {}),
                                  }}
                                >
                                  <input
                                    type="radio"
                                    name={`row-${m.id}`}
                                    value={opt.val}
                                    checked={isActive}
                                    onChange={() =>
                                      setRowOverride(m.id, opt.val)
                                    }
                                    disabled={saving || selected.length === 0}
                                    style={S.radioInput}
                                  />
                                  {opt.label}
                                </label>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}

          <div style={S.rightFooter}>
            {savedAt && (
              <span style={S.savedText}>
                저장 완료: {savedAt.toLocaleTimeString("ko-KR")}
              </span>
            )}
            <span style={S.changesBadge}>{touchedCount}개 변경</span>
            <button
              onClick={handleResetToInherit}
              style={S.btnReset}
              disabled={saving || selected.length === 0}
            >
              상속으로 초기화
            </button>
            <button
              onClick={handleSave}
              style={S.btnPrimary}
              disabled={saving || selected.length === 0 || touchedCount === 0}
            >
              {saving ? "저장 중..." : `${selected.length}개 대상에 저장`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const S = {
  root: {
    padding: "16px 20px",
    height: "calc(100vh - 56px)",
    display: "flex",
    flexDirection: "column",
    fontFamily: "'Pretendard', -apple-system, sans-serif",
  },
  header: { marginBottom: 8 },
  title: { fontSize: 18, fontWeight: 700, margin: "0 0 4px", color: "#1f2937" },
  subtitle: { fontSize: 12, color: "#6b7280", lineHeight: 1.5, margin: 0 },
  code: {
    background: "#f3f4f6",
    padding: "1px 5px",
    borderRadius: 3,
    fontSize: 11,
    fontFamily: "Menlo, monospace",
    color: "#374151",
  },
  error: {
    padding: "8px 12px",
    borderRadius: 6,
    background: "#fef2f2",
    color: "#dc2626",
    border: "1px solid #fecaca",
    fontSize: 13,
    margin: "8px 0",
  },

  body: { flex: 1, display: "flex", gap: 12, minHeight: 0 },

  left: {
    width: 420,
    display: "flex",
    flexDirection: "column",
    border: "1px solid #e5e7eb",
    borderRadius: 8,
    background: "#fff",
    minHeight: 0,
  },
  leftTabs: { display: "flex", borderBottom: "1px solid #e5e7eb" },
  leftTab: {
    flex: 1,
    padding: "10px 0",
    background: "none",
    border: "none",
    cursor: "pointer",
    fontSize: 13,
    color: "#6b7280",
  },
  leftTabActive: {
    color: "#18181b",
    borderBottom: "2px solid #18181b",
    fontWeight: 600,
  },
  leftToolbar: { display: "flex", gap: 6, padding: "8px 8px 6px" },
  search: {
    flex: 1,
    padding: "6px 10px",
    border: "1px solid #e5e7eb",
    borderRadius: 6,
    fontSize: 12,
    outline: "none",
  },
  btnGhost: {
    padding: "5px 10px",
    border: "1px solid #e5e7eb",
    borderRadius: 6,
    background: "#fff",
    color: "#374151",
    fontSize: 11,
    cursor: "pointer",
    fontWeight: 500,
  },
  leftCount: { padding: "0 12px 6px", fontSize: 11, color: "#6b7280" },
  hint: { color: "#9ca3af" },
  leftList: { flex: 1, overflowY: "auto", borderTop: "1px solid #f3f4f6" },
  leftTable: { width: "100%", borderCollapse: "collapse", fontSize: 12 },
  thCheck: {
    width: 28,
    padding: "6px 4px",
    textAlign: "center",
    background: "#fafafa",
    borderBottom: "1px solid #e5e7eb",
    fontWeight: 600,
    color: "#374151",
    position: "sticky",
    top: 0,
  },
  thCode: {
    width: 70,
    padding: "6px 8px",
    textAlign: "left",
    background: "#fafafa",
    borderBottom: "1px solid #e5e7eb",
    fontWeight: 600,
    color: "#374151",
    position: "sticky",
    top: 0,
  },
  thName: {
    padding: "6px 8px",
    textAlign: "left",
    background: "#fafafa",
    borderBottom: "1px solid #e5e7eb",
    fontWeight: 600,
    color: "#374151",
    position: "sticky",
    top: 0,
  },
  thMeta: {
    width: 110,
    padding: "6px 8px",
    textAlign: "left",
    background: "#fafafa",
    borderBottom: "1px solid #e5e7eb",
    fontWeight: 600,
    color: "#374151",
    position: "sticky",
    top: 0,
  },
  tdCheck: {
    padding: "4px 4px",
    textAlign: "center",
    borderBottom: "1px solid #f3f4f6",
  },
  tdCode: {
    padding: "4px 8px",
    borderBottom: "1px solid #f3f4f6",
    color: "#6b7280",
    fontFamily: "Menlo, monospace",
    fontSize: 11,
  },
  tdName: {
    padding: "4px 8px",
    borderBottom: "1px solid #f3f4f6",
    color: "#1f2937",
  },
  tdMeta: {
    padding: "4px 8px",
    borderBottom: "1px solid #f3f4f6",
    color: "#6b7280",
    fontSize: 11,
  },
  trSelected: { background: "#eff6ff" },
  checkbox: { width: 14, height: 14, cursor: "pointer" },
  emptyRow: {
    padding: 40,
    textAlign: "center",
    color: "#9ca3af",
    fontSize: 13,
  },

  thFilterCell: {
    padding: "4px 4px",
    background: "#f9fafb",
    borderBottom: "1px solid #e5e7eb",
    position: "sticky",
    top: 27,
  },
  filterInput: {
    width: "100%",
    padding: "3px 6px",
    border: "1px solid #e5e7eb",
    borderRadius: 4,
    fontSize: 11,
    outline: "none",
    background: "#fff",
    boxSizing: "border-box",
  },
  metaLink: {
    textDecoration: "underline dotted",
    textUnderlineOffset: 2,
    color: "#2563eb",
  },
  popover: {
    position: "absolute",
    top: "100%",
    right: 0,
    marginTop: 4,
    minWidth: 220,
    maxWidth: 320,
    maxHeight: 280,
    overflowY: "auto",
    background: "#fff",
    border: "1px solid #d1d5db",
    borderRadius: 6,
    boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
    zIndex: 100,
    padding: 0,
    cursor: "default",
    whiteSpace: "normal",
    textAlign: "left",
  },
  popoverHeader: {
    padding: "6px 10px",
    background: "#fafafa",
    borderBottom: "1px solid #e5e7eb",
    fontSize: 11,
    fontWeight: 600,
    color: "#374151",
  },
  popoverList: { display: "flex", flexDirection: "column" },
  popoverItem: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "5px 10px",
    borderBottom: "1px solid #f3f4f6",
    fontSize: 11,
  },
  popoverUserId: {
    fontFamily: "Menlo, monospace",
    fontSize: 10,
    color: "#6b7280",
    background: "#f3f4f6",
    padding: "1px 5px",
    borderRadius: 3,
  },
  popoverUserName: {
    color: "#1f2937",
    display: "flex",
    alignItems: "center",
    gap: 4,
  },
  tagManager: {
    fontSize: 9,
    fontWeight: 600,
    color: "#92400e",
    marginLeft: 4,
  },

  right: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    border: "1px solid #e5e7eb",
    borderRadius: 8,
    background: "#fff",
    minHeight: 0,
  },
  rightHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "10px 16px",
    borderBottom: "1px solid #e5e7eb",
  },
  rightTitle: { fontSize: 14, fontWeight: 600, color: "#1f2937" },
  rightBadge: {
    marginLeft: 8,
    padding: "2px 8px",
    background: "#18181b",
    color: "#fff",
    fontSize: 11,
    fontWeight: 600,
    borderRadius: 4,
  },
  rightHint: { fontSize: 12, color: "#9ca3af" },
  loading: { padding: 60, textAlign: "center", color: "#9ca3af" },

  menuPanel: { flex: 1, overflowY: "auto", padding: 12 },
  catBlock: {
    marginBottom: 12,
    border: "1px solid #f3f4f6",
    borderRadius: 8,
    overflow: "hidden",
  },
  catHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "8px 12px",
    background: "#fafafa",
    fontSize: 13,
    fontWeight: 600,
    color: "#374151",
    gap: 8,
  },
  catActions: { display: "flex", gap: 4 },
  catBtn: {
    padding: "3px 8px",
    border: "1px solid #e5e7eb",
    borderRadius: 4,
    background: "#fff",
    color: "#6b7280",
    fontSize: 11,
    fontWeight: 500,
    cursor: "pointer",
  },
  catItems: { display: "flex", flexDirection: "column" },
  menuRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "8px 12px",
    borderTop: "1px solid #f3f4f6",
    gap: 12,
  },
  menuRowTouched: { background: "#fffbeb" },
  menuRowMixed: { background: "#fafafa" },
  menuRowText: { flex: 1, minWidth: 0 },
  menuLabel: {
    fontSize: 13,
    fontWeight: 500,
    color: "#111827",
    marginBottom: 2,
  },
  menuId: {
    fontSize: 10,
    color: "#9ca3af",
    fontFamily: "Menlo, monospace",
    background: "#fafafa",
    padding: "1px 5px",
    borderRadius: 3,
  },
  tagDefaultOn: {
    marginLeft: 8,
    padding: "1px 5px",
    background: "#d1fae5",
    color: "#065f46",
    fontSize: 10,
    fontWeight: 600,
    borderRadius: 3,
  },
  tagMixed: {
    marginLeft: 8,
    padding: "1px 5px",
    background: "#e0e7ff",
    color: "#3730a3",
    fontSize: 10,
    fontWeight: 600,
    borderRadius: 3,
  },
  tagTouched: {
    marginLeft: 8,
    padding: "1px 5px",
    background: "#fde68a",
    color: "#92400e",
    fontSize: 10,
    fontWeight: 600,
    borderRadius: 3,
  },
  radioGroup: { display: "flex", gap: 4 },
  radioOpt: {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    padding: "4px 8px",
    border: "1px solid #e5e7eb",
    borderRadius: 4,
    cursor: "pointer",
    fontSize: 11,
    color: "#6b7280",
    background: "#fff",
    whiteSpace: "nowrap",
  },
  radioInput: { width: 11, height: 11, cursor: "pointer" },

  rightFooter: {
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    gap: 8,
    padding: "10px 16px",
    borderTop: "1px solid #e5e7eb",
  },
  savedText: { fontSize: 11, color: "#10b981" },
  changesBadge: {
    marginRight: 8,
    padding: "3px 8px",
    background: "#fef3c7",
    color: "#92400e",
    fontSize: 11,
    fontWeight: 600,
    borderRadius: 4,
  },
  btnReset: {
    padding: "7px 12px",
    border: "1px solid #fecaca",
    borderRadius: 6,
    background: "#fff",
    color: "#dc2626",
    fontSize: 12,
    fontWeight: 500,
    cursor: "pointer",
  },
  btnPrimary: {
    padding: "7px 16px",
    border: "none",
    borderRadius: 6,
    background: "#18181b",
    color: "#fff",
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
  },
};

export default AdminMenuVisibility;
